import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { syllabusApi } from "../config/syllabusApi";
import "../QuizZone/QuizZonePage.css";
import "../Syllabus/syllabus.css";

const SITE_URL = "https://www.aamschool.in";
const TEST_SERIES_TOPIC = "Test Series";

const normalizeItems = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.content)) return value.content;
  }
  return [];
};

const pickFirstValue = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value != null && value !== "") return String(value).trim();
  }
  return "";
};

const normalizeExam = (value) => {
  const base = value && typeof value === "object" ? value : {};
  const content =
    base?.content && typeof base.content === "object" && !Array.isArray(base.content)
      ? base.content
      : {};
  const syllabus =
    base?.syllabus && typeof base.syllabus === "object" && !Array.isArray(base.syllabus)
      ? base.syllabus
      : {};

  return {
    id: base?.id || pickFirstValue(content?.id, syllabus?.id),
    examName: pickFirstValue(
      base?.examName,
      base?.exam_name,
      content?.examName,
      content?.exam_name,
      syllabus?.examName,
      syllabus?.exam_name,
      base?.title
    ),
    examCode: pickFirstValue(
      base?.examCode,
      base?.exam_code,
      base?.examKey,
      content?.examCode,
      content?.exam_code,
      content?.examKey,
      syllabus?.examCode,
      syllabus?.exam_code,
      syllabus?.examKey
    ),
    updatedAt: pickFirstValue(
      base?.updatedAt,
      base?.updated_at,
      content?.updatedAt,
      syllabus?.updatedAt
    ),
  };
};

function TestSeriesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canonicalUrl = `${SITE_URL}/test-series`;

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await syllabusApi.latest();
        if (!mounted) return;
        setItems(normalizeItems(data).map(normalizeExam).filter((item) => item.examName));
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "Unable to load test series.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedItems = useMemo(
    () =>
      [...items].sort((left, right) =>
        String(left?.examName || "").localeCompare(String(right?.examName || ""))
      ),
    [items]
  );

  return (
    <div className="quiz-zone-page">
      <Helmet>
        <title>Test Series | AAMSchool</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta
          name="description"
          content="Browse exam-wise test series on AAMSchool and start full tests, sectional tests, and PYQ tests from one place."
        />
        <meta property="og:title" content="Test Series | AAMSchool" />
        <meta
          property="og:description"
          content="Browse exam-wise test series on AAMSchool and start full tests, sectional tests, and PYQ tests from one place."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="quiz-zone-hero">
        <h1>Test Series</h1>
        <p>Choose your exam and continue with Full Tests, Sectional Tests, or PYQ Tests.</p>
      </div>

      <div className="syllabus-list-shell">
        {loading ? <p>Loading test series...</p> : null}
        {error ? <p className="syllabus-error">{error}</p> : null}
        {!loading && !error && !sortedItems.length ? (
          <p className="syllabus-empty-state">No test series exams are available right now.</p>
        ) : null}
        {!loading && !error && sortedItems.length ? (
          <div className="syllabus-list">
            {sortedItems.map((item, index) => {
              const subject = item.examCode || item.examName;
              const path = `/quiz-zone/${encodeURIComponent(subject)}/${encodeURIComponent(
                TEST_SERIES_TOPIC
              )}/quizzes`;

              return (
                <article
                  className="syllabus-list-item"
                  key={`${item.examCode || item.examName || "test-series"}-${index}`}
                >
                  <div>
                    <h3>
                      <Link to={path}>{item.examName}</Link>
                    </h3>
                    <div className="syllabus-meta">
                      <span className="syllabus-meta-chip">
                        Code: {item.examCode || "NA"}
                      </span>
                      <span className="syllabus-meta-chip">
                        Topic: {TEST_SERIES_TOPIC}
                      </span>
                      {item.updatedAt ? (
                        <span className="syllabus-meta-chip">Updated: {item.updatedAt}</span>
                      ) : null}
                    </div>
                  </div>
                  <Link className="syllabus-view-link" to={path}>
                    Open Test Series
                  </Link>
                </article>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TestSeriesPage;
